import React, { useState, useRef, useEffect, useCallback } from "react";
import { FiUpload } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

// Imports for upload functionality
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { RingLoader } from 'react-spinners';
import { uploadVideo, getStatus, getComscore, getPresignedUrl } from "../Apis/endpoints";
import Completedpop from "./completed-popup";
import ProgressBar from "./ProgressBar";
import Processesimg from "../Components/Imagessvg/processimg";

const UploadPage = () => {
    const navigate = useNavigate();
    const [mode, setMode] = useState('fm');

    // == START: State and Logic ==
    const ffmpeg = useRef(new FFmpeg());
    const fileInputRef = useRef(null);
    const [ffmpegLoaded, setFFmpegLoaded] = useState(false);
    const [status, setStatus] = useState('idle'); // 'idle', 'processing', 'completed'
    const [isUploading, setIsUploading] = useState(false); // Controls content inside the modal
    const [uploadedVideoId, setUploadedVideoId] = useState(null);
    const [completedVideoId, setCompletedVideoId] = useState(null);
    const [comscore, setComscore] = useState(null);
    const [video_type] = useState('uploaded');

    useEffect(() => {
        const loadFFmpeg = async () => {
            const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.2/dist/umd';
            ffmpeg.current.on('log', ({ message }) => console.log(message));
            try {
                await ffmpeg.current.load({
                    coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
                    wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
                });
                setFFmpegLoaded(true);
                console.log("FFmpeg loaded successfully.");
            } catch (error) {
                console.error("Failed to load FFmpeg in UploadPage:", error);
                alert("Could not initialize the upload component. Please refresh the page.");
            }
        };
        loadFFmpeg();
    }, []);

    const processAudioToMp3 = useCallback(async (file) => {
        if (!ffmpegLoaded) throw new Error("FFmpeg not loaded.");
        console.log("Processing audio to MP3...");
        await ffmpeg.current.writeFile('input_audio', await fetchFile(file));
        await ffmpeg.current.exec(['-i', 'input_audio', '-acodec', 'libmp3lame', '-b:a', '128k', 'output.mp3']);
        const data = await ffmpeg.current.readFile('output.mp3');
        return new File([data], `${file.name.split('.')[0]}.mp3`, { type: 'audio/mpeg' });
    }, [ffmpegLoaded]);

    const pollStatus = async (videoId) => {
        const interval = setInterval(async () => {
            try {
                const statusResponse = await getStatus(videoId);
                if (statusResponse && statusResponse.status === "completed") {
                    clearInterval(interval);
                    const score = await getComscore(videoId);
                    setComscore(score);
                    setCompletedVideoId(videoId);
                    setStatus('completed');
                } else {
                    console.log("Polling: Still processing...");
                }
            } catch (error) {
                console.error("Error polling status:", error);
                clearInterval(interval);
                alert("Error checking video processing status. Please try again later.");
                setStatus('idle');
            }
        }, 5000);
    };

    const handleUpload = async (file) => {
        if (!file) return;

        setStatus('processing'); 
        setIsUploading(true); 
        console.log("Initiating file upload...");

        try {
            const presignedData = await getPresignedUrl(mode, file.type);
            if (!presignedData?.url || !presignedData?.file_name) {
                throw new Error("Failed to get pre-signed URL.");
            }
            
            const match = presignedData.file_name.match(/\/([^/]+)\.\w+$/);
            const videoId = match ? match[1] : null;
            if (!videoId) {
                throw new Error(`Failed to extract video_id from file name: ${presignedData.file_name}`);
            }
            setUploadedVideoId(videoId);

            const uploadResponse = await fetch(presignedData.url, {
                method: "PUT",
                body: file,
                headers: { "Content-Type": file.type },
            });

            if (!uploadResponse.ok) {
                throw new Error(`Error uploading to S3: ${uploadResponse.statusText}`);
            }

            console.log("Successfully uploaded to S3. Notifying backend.");
            setIsUploading(false); 
            
            const formData = new FormData();
            formData.append("video_files", file.name);
            formData.append("video_urls", presignedData.file_name);
            formData.append("video_id", videoId);
            formData.append("mode", mode);
            formData.append("video_type", video_type);

            const response = await uploadVideo(formData);
            console.log("Backend response:", response);

            if (response?.video_id) {
                await pollStatus(response.video_id);
            } else {
                throw new Error("No video_id received from backend API.");
            }
        } catch (error) {
            console.error("Overall upload process failed:", error);
            alert(`Error during upload: ${error.message}`);
            setStatus('idle');
            setIsUploading(false);
            setUploadedVideoId(null);
        }
    };
    
    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        let allowedTypes, maxDuration, processedFile = file;

        if (mode === 'audio') {
            allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg'];
            maxDuration = 121;
        } else {
            allowedTypes = ['video/mp4'];
            maxDuration = 121;
        }

        if (!allowedTypes.includes(file.type) && !file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
            alert(`Only ${allowedTypes.join(', ')} files are allowed for ${mode} mode!`);
            event.target.value = "";
            return;
        }

        if (file.size > 200 * 1024 * 1024) {
            alert("File size exceeds 200MB limit!");
            event.target.value = "";
            return;
        }

        const media = document.createElement(mode === 'audio' ? "audio" : "video");
        media.preload = "metadata";
        media.onloadedmetadata = async () => {
            if (media.duration > maxDuration) {
                alert(`File duration exceeds ${Math.floor(maxDuration / 60)} minute limit!`);
                event.target.value = "";
                return;
            }

            if (mode === 'audio' && ffmpegLoaded) {
                try {
                    processedFile = await processAudioToMp3(file);
                } catch (error) {
                    alert("Failed to process audio file. Please try again.");
                    event.target.value = "";
                    return;
                }
            }
            
            handleUpload(processedFile);
        };
        media.onerror = () => {
            alert("Error loading file metadata!");
            event.target.value = "";
        };
        media.src = URL.createObjectURL(file);
        event.target.value = "";
    };

    const handleUploadButtonClick = () => {
        if (!ffmpegLoaded) {
            alert("Upload component is not ready yet. Please wait a moment.");
            return;
        }
        fileInputRef.current.click();
    };

/*********************************************************************************/
/*                            No changes above this line                         */
/*********************************************************************************/
    const handleStartRecording = () => {
       navigate(`/video?mode=${mode}`);
    };

    return (
        <div className="min-h-full w-full flex flex-col items-center p-4 md:p-6">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept={mode === 'audio' ? 'audio/mpeg,audio/wav,audio/ogg' : 'video/mp4'}
              onChange={handleFileSelect}
            />

            {/* Main Page Content */}
            <div className="w-full">
                <p className="text-2xl font-semibold font-sans">Assessment</p>
            </div>
            <div className="w-full h-full flex flex-col items-center justify-center p-8">
                 {/* Mode selection cards */}
                <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl">
                    <div
                        className={`relative w-full h-80 rounded-lg flex flex-col items-left bg-[#F8F8F8] p-4 transition-all duration-300 ${mode === "fm" ? "shadow-[0_0_10px_2px_#2563EB47]" : "hover:shadow-[0_0_10px_2px_#2563EB47]"}`}
                        onClick={() => setMode("fm")}
                    >
                        <div><input type="radio" name="mode" value="fm" checked={mode === "fm"} onChange={() => setMode("fm")}/></div>
                        <label className="mt-2 flex items-left gap-2 flex-col"><span className="text-lg font-semibold">Face Mode</span><span className="text-sm text-gray-600 text-left">Practice using facial expressions to improve your communication and make it more engaging.</span></label>
                        <div className="absolute bottom-4 right-4 w-40 h-40 flex items-end justify-end"><img src="/images/FaceMode.svg" alt="Face Mode" className="w-full h-full object-contain" /></div>
                    </div>
                    <div
                        className={`relative w-full h-80 rounded-lg flex flex-col items-left bg-[#F8F8F8] p-4 transition-all duration-300 ${mode === "bm" ? "shadow-[0_0_10px_2px_#2563EB47]" : "hover:shadow-[0_0_10px_2px_#2563EB47]"}`}
                        onClick={() => setMode("bm")}
                    >
                        <div><input type="radio" name="mode" value="bm" checked={mode === "bm"} onChange={() => setMode("bm")} /></div>
                        <label className="mt-2 flex items-left gap-2 flex-col"><div className="ml-2 flex flex-col"><span className="text-lg font-semibold">Body Mode</span><span className="text-sm text-gray-600 text-left">Observe how you use gestures and body language to enhance your communication content.</span></div></label>
                        <div className="absolute bottom-4 right-4 w-40 h-40 flex items-end justify-end"><img src="/images/BodyMode.svg" alt="Body Mode" className="w-90 h-90 object-contain" /></div>
                    </div>
                    <div
                        className={`relative w-full h-80 rounded-lg flex flex-col items-left bg-[#F8F8F8] p-4 transition-all duration-300 ${mode === "audio" ? "shadow-[0_0_10px_2px_#2563EB47]" : "hover:shadow-[0_0_10px_2px_#2563EB47]"}`}
                        onClick={() => setMode("audio")}
                    >
                        <div><input type="radio" name="mode" value="audio" checked={mode === "audio"} onChange={() => setMode("audio")} /></div>
                        <label className="mt-2 flex items-left gap-2 flex-col"><div className="ml-2 flex flex-col"><span className="text-lg font-semibold">Audio Mode</span><span className="text-sm text-gray-600 text-left">Concentrate on your voice to enhance clarity, tone, and confidence while speaking.</span></div></label>
                        <div className="absolute bottom-4 right-4 w-40 h-40 flex items-end justify-end"><img src="/images/AudioMode.svg" alt="Audio Mode" className="w-90 h-90 object-contain" /></div>
                    </div>
                </div>
                {/* Buttons */}
                <div className="flex flex-col md:flex-row gap-4 w-full max-w-md mt-10">
                    <button className="w-full h-[80%] flex items-center justify-center bg-[#2563EB] font-[sans archivo] text-white py-3 px-6 rounded-full hover:bg-blue-600 transition duration-300 text-lg" onClick={handleStartRecording} style={{ fontFamily: "sans-serif, Archivo" }}>
                        <svg className="me-2 w-5 h-5 sm:w-6 sm:h-6 " width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg"><g opacity="0.8" filter="url(#filter0_f_6752_8566)"><path d="M6.5 2C4.01863 2 2 4.01863 2 6.5C2 8.98138 4.01863 11 6.5 11C8.98138 11 11 8.98138 11 6.5C11 4.01863 8.98138 2 6.5 2ZM6.5 8.375C6.00272 8.375 5.52581 8.17746 5.17417 7.82583C4.82254 7.47419 4.625 6.99728 4.625 6.5C4.625 6.00272 4.82254 5.52581 5.17417 5.17417C5.52581 4.82254 6.00272 4.625 6.5 4.625C6.99728 4.625 7.47419 4.82254 7.82583 5.17417C8.17746 5.52581 8.375 6.00272 8.375 6.5C8.375 6.99728 8.17746 7.47419 7.82583 7.82583C7.47419 8.17746 6.99728 8.375 6.5 8.375Z" fill="white" /></g><circle cx="6.5" cy="6.5" r="2.5" fill="white" /><defs><filter id="filter0_f_6752_8566" x="0" y="0" width="13" height="13" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix" /><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" /><feGaussianBlur stdDeviation="1" result="effect1_foregroundBlur_6752_8566" /></filter></defs></svg>
                        Start Recording
                    </button>
                    <button className="w-full h-[80%] flex items-center justify-center bg-white font-[sans Archivo] text-[#2563EB] border-2 border-[#2563EB] py-3 px-6 rounded-full hover:bg-blue-100 transition duration-300 text-lg disabled:opacity-50 disabled:cursor-wait" onClick={handleUploadButtonClick} style={{ fontFamily: "sans-serif, Archivo" }} disabled={!ffmpegLoaded || status !== 'idle'}>
                        <FiUpload className="me-2 w-5 h-5 sm:w-5 h-5 md:w-5 md: h-5" />
                        {mode === 'audio' ? 'Upload Audio' : 'Upload Video'}
                    </button>
                </div>
            </div>

            {/* Modal for Processing */}
            {status === 'processing' && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 z-50">
                    <div className="bg-white p-4 sm:p-6 md:p-8 rounded-3xl shadow-lg w-full max-w-4xl min-h-[30rem] flex flex-col justify-center items-center">
                        <Processesimg />
                        <p className="w-4/5 sm:w-3/5 text-center text-[#ABABAB] mt-4 sm:mt-5 text-xs sm:text-sm md:text-base" style={{ fontFamily: "sans-serif, Archivo" }}>
                            Sit back and relax while we process your {mode === 'audio' ? 'audio' : 'video'}! We'll let you know once it's ready.
                        </p>
                        
                        {/* ======================= KEY CHANGE HERE ======================= */}
                        {/* Show ProgressBar ONLY for video modes (fm, bm) AFTER S3 upload is complete. */}
                        {/* For audio mode, or during the initial upload of any file, show the RingLoader. */}
                        
                        {!isUploading && uploadedVideoId && (mode === 'fm' || mode === 'bm') ? (
                            // Case 1: Video processing (after upload). Show progress bar.
                            <ProgressBar videoId={uploadedVideoId} />
                        ) : (
                            // Case 2: Audio processing OR any file currently uploading. Show spinner.
                            <div className="flex flex-col justify-center items-center mt-10">
                                <RingLoader color="#2563EB" loading={true} size={60} />
                                <p className="text-sm text-gray-600 mt-4">
                                    {isUploading ? 'Uploading your file...' : `Processing your ${mode}...`}
                                </p>
                            </div>
                        )}
                        {/* ===================== END OF KEY CHANGE ===================== */}

                    </div>
                </div>
            )}

            {/* Modal for Completion */}
            {status === 'completed' && (
                <Completedpop
                    Mode={mode}
                    videoId={completedVideoId}
                    comscore={comscore}
                    onClose={() => {
                        setStatus('idle');
                        setCompletedVideoId(null);
                        setUploadedVideoId(null);
                        setComscore(null);
                    }}
                />
            )}
        </div>
    );
};

export default UploadPage;